import React, { useEffect, useRef, useState } from 'react';

function TextElement({ element, isPreviewMode, onUpdate }) {
  const { content, style = {} } = element;
  const textRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Add responsive data attributes to text elements for better mobile support
  useEffect(() => {
    if (textRef.current) {
      // Store original position data for responsive layouts
      const parentElement = textRef.current.closest('.element-wrapper');
      if (parentElement) {
        parentElement.dataset.elementContent = 'text';
        parentElement.dataset.contentPriority = style.priority || 'normal';
      }
    }
  }, [style.priority]);
  
  // Handle double-click to edit
  const handleDoubleClick = (e) => {
    if (isPreviewMode) return;
    e.stopPropagation();
    setIsEditing(true);
    
    // Make the element editable
    if (textRef.current) {
      textRef.current.contentEditable = true;
      textRef.current.focus();
      
      // Select all text for quick editing
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(textRef.current);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };
  
  const handleTextChange = (e) => {
    // Create a new element with updated content
    const updatedElement = {
      ...element,
      content: e.target.innerHTML,
      lastUpdated: Date.now()
    };
    
    // Pass the updated element up to the parent
    if (onUpdate) {
      onUpdate(updatedElement);
    }
    
    // Exit edit mode
    setIsEditing(false);
    
    // Make sure to blur the element to exit contentEditable mode
    if (textRef.current) {
      textRef.current.contentEditable = false;
      textRef.current.blur();
    }
  };

  const textStyle = {
    color: style.color || '#000000',
    fontFamily: style.fontFamily || 'Arial, sans-serif',
    fontSize: style.fontSize || '16px',
    fontWeight: style.fontWeight || 'normal',
    lineHeight: style.lineHeight || '1.5',
    textAlign: style.textAlign || 'left',
    letterSpacing: style.letterSpacing || 'normal',
    padding: style.padding || '0',
    width: '100%',
    height: '100%',
    margin: '0',
    // Add CSS variable for responsive text scaling
    '--base-font-size': style.fontSize || '16px',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    cursor: isPreviewMode ? 'default' : (isEditing ? 'text' : 'move')
  };

  return (
    <>
      {!isPreviewMode && !isEditing && (
        <div className="edit-hint">Double-click to edit text</div>
      )}
      <div 
        ref={textRef}
        className={`text-content ${isEditing ? 'editing' : ''}`}
        style={textStyle}
        contentEditable={isEditing}
        suppressContentEditableWarning={true}
        dangerouslySetInnerHTML={{ __html: content || 'Edit this text' }}
        onDoubleClick={handleDoubleClick}
        onBlur={isEditing ? handleTextChange : null}
      />
    </>
  );
}

export default TextElement; 